import InvestingAvatar from 'assets/images/investing-avatar.png';
import { useDialog } from 'common/hooks/useDialog';
import { useNotification } from 'common/hooks/useNotification';
import { usePeriodicityOptions } from 'common/hooks/usePeriodicityOptions';
import { formatCurrency, formatIban, formatTransactionAmount, normalizeIban } from 'common/utils/formatUtils';
import { format } from 'date-fns';
import {
  FinancialProductType,
  InvestmentProductFragment,
  useInvestmentProductQuery,
  useUpdateInvestmentProductMutation,
} from 'generated/graphql';
import { Path } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ProductDetail } from '../ProductDetail/ProductDetail';
import { QRCodeDialog, QRCodeDialogProps } from '../QRCodeDialog/QRCodeDialog';
import { QRCodeMissingDataDialog, QRCodeMissingDataDialogProps } from '../QRCodeDialog/QRCodeMissingDataDialog';
import { ButtonRowType, DetailRowType, Section } from '../types';
import { ProductDetailDialogProps } from '../useProductDetail';
import { mapInvestmentProductToInput } from './utils';

export const InvestmentProductDetail: React.FC<ProductDetailDialogProps> = ({ id, ...props }) => {
  const { i18n, t } = useTranslation();
  const notify = useNotification();

  const { data } = useInvestmentProductQuery({
    variables: {
      id,
    },
  });

  const qrCodeDialog = useDialog<QRCodeDialogProps, void>(QRCodeDialog, {
    instanceId: 'qr-code-dialog',
    tmpNavigable: true,
  });

  const qrCodeMissingDataDialog = useDialog<QRCodeMissingDataDialogProps, void>(QRCodeMissingDataDialog, {
    instanceId: 'qr-code-missing-data-dialog',
  });

  const handleQRPayment = () => {
    const iban = data?.detail.paymentInfo?.ibanForPayment;
    const amount = data?.detail.paymentPeriod?.expectedAmount?.amount;
    if (iban && amount) {
      qrCodeDialog.create({
        amount,
        constantSymbol: data?.detail.paymentInfo?.constantSymbol ?? undefined,
        currency: data?.detail.paymentPeriod?.expectedAmount?.currency ?? 'EUR',
        iban,
        specificSymbol: data?.detail.paymentInfo?.specificSymbol ?? undefined,
        variableSymbol: data?.detail.paymentInfo?.variableSymbol ?? undefined,
      });
    } else {
      qrCodeMissingDataDialog.create({});
    }
  };

  const periodicityOptions = usePeriodicityOptions();

  const content: Section<InvestmentProductFragment, Path<InvestmentProductFragment>>[] = [
    {
      rows: [
        {
          inputType: 'text',
          label: t('products:investment.detail.institutionName'),
          path: 'institutionName',
          skip: !!data?.detail.institution?.id,
        } as DetailRowType<InvestmentProductFragment, 'institutionName'>,
        {
          inputType: 'text',
          label: t('products:investment.detail.productName'),
          path: 'name',
        } as DetailRowType<InvestmentProductFragment, 'name'>,
        {
          inputType: 'text',
          label: t('products:investment.detail.financialInstrument'),
          path: 'financialInstrument',
        } as DetailRowType<InvestmentProductFragment, 'financialInstrument'>,
        {
          inputType: 'text',
          label: t('products:investment.detail.contractNumber'),
          path: 'contractNumber',
        } as DetailRowType<InvestmentProductFragment, 'contractNumber'>,
        {
          formatter: (value: Date) => format(value, 'd. M. yyyy'),
          inputType: 'date',
          label: t('products:investment.detail.contractSignDate'),
          path: 'contractSignDate',
        } as DetailRowType<InvestmentProductFragment, 'contractSignDate'>,
      ],
      title: '',
    },
    {
      rows: [
        {
          formatter: (value: number) =>
            formatTransactionAmount(value, i18n.language, 2, data?.detail.paymentPeriod?.expectedAmount?.currency),
          inputEndAdornment: data?.detail.paymentPeriod?.expectedAmount?.currency,
          inputType: 'amount',
          label: t('products:investment.detail.amountRegularInvestment'),
          // we are storing paymentPeriod.expectedAmount.amount as negative number
          // but we want user to type a positive equivalent
          parse: (value) => -value,
          path: 'paymentPeriod.expectedAmount.amount',
          transform: (value) => -value,
        } as DetailRowType<InvestmentProductFragment, 'paymentPeriod.expectedAmount.amount'>,
        {
          formatter: (value: Date) => format(value, 'd. M. yyyy'),
          inputType: 'date',
          label: t('products:investment.detail.nextPayment'),
          path: 'paymentPeriod.expectedDate',
        } as DetailRowType<InvestmentProductFragment, 'paymentPeriod.expectedDate'>,
        {
          formatter: (value) =>
            periodicityOptions.find(
              (option) =>
                option.value.periodCount === value.periodCount && option.value.periodTypeCode === value.periodTypeCode,
            )?.label ?? '',
          inputType: 'radioSelect',
          label: t('products:investment.detail.periodType'),
          options: periodicityOptions,
          parse: (value) => ({
            expectedAmount: data?.detail.paymentPeriod?.expectedAmount,
            expectedDate: data?.detail.paymentPeriod?.expectedDate,
            periodCount: value.periodCount,
            periodTypeCode: value.periodTypeCode,
          }),
          path: 'paymentPeriod',
          transform: (value) => ({
            periodCount: value.periodCount,
            periodTypeCode: value.periodTypeCode,
          }),
        } as DetailRowType<InvestmentProductFragment, 'paymentPeriod'>,
      ],
      title: '',
    },
    {
      rows: [
        {
          inputType: 'variableSymbol',
          label: t('products:investment.detail.variableSymbol'),
          path: 'paymentInfo.variableSymbol',
        } as DetailRowType<InvestmentProductFragment, 'paymentInfo.variableSymbol'>,
        {
          inputType: 'specificSymbol',
          label: t('products:investment.detail.specificSymbol'),
          path: 'paymentInfo.specificSymbol',
        } as DetailRowType<InvestmentProductFragment, 'paymentInfo.specificSymbol'>,
        {
          inputType: 'constantSymbol',
          label: t('products:investment.detail.constantSymbol'),
          path: 'paymentInfo.constantSymbol',
        } as DetailRowType<InvestmentProductFragment, 'paymentInfo.constantSymbol'>,
        {
          formatter: formatIban,
          inputType: 'iban',
          label: t('products:investment.detail.ibanForPayment'),
          parse: normalizeIban,
          path: 'paymentInfo.ibanForPayment',
        } as DetailRowType<InvestmentProductFragment, 'paymentInfo.ibanForPayment'>,
        {
          color: 'blue.dark',
          label: t('products:investment.detail.qrCode'),
          onClick: handleQRPayment,
        } as ButtonRowType,
      ],
      title: t('products:investment.detail.paymentInfo.title'),
    },
    {
      rows: [
        {
          formatter: (value: number) => formatCurrency(value ?? 0, i18n.language),
          inputEndAdornment: t('common:inputEndAdornment.eur'),
          inputType: 'amount',
          label: t('products:investment.detail.amountInvested'),
          path: 'amountInvested.amount',
        } as DetailRowType<InvestmentProductFragment, 'amountInvested.amount'>,
        {
          formatter: (value: number) => formatCurrency(value ?? 0, i18n.language),
          inputEndAdornment: t('common:inputEndAdornment.eur'),
          inputType: 'amount',
          label: t('products:investment.detail.currentMarketValue'),
          path: 'currentMarketValue.amount',
        } as DetailRowType<InvestmentProductFragment, 'currentMarketValue.amount'>,
      ],
      title: t('products:investment.detail.financialDetails.title', {
        date: format(data?.detail.financialDetailsUpdatedAt ?? new Date(), 'd. M. yyyy'),
      }),
    },
  ];

  const [updateProduct] = useUpdateInvestmentProductMutation({
    onError: () => {
      notify({ message: t('common:somethingWentWrong'), type: 'error' });
    },
    refetchQueries: ['CalendarEventInstances', 'Notifications', 'NotificationsCount'],
  });

  const handleUpdate = async (value: InvestmentProductFragment) => {
    await updateProduct({ variables: { id, updatedProduct: mapInvestmentProductToInput(value) } });
  };

  return (
    <ProductDetail<InvestmentProductFragment>
      {...props}
      contactExpert={{
        avatarSrc: InvestingAvatar,
        email: t('products:contacts.investment.email'),
        jobTitle: t('products:contacts.investment.jobTitle'),
        name: t('products:contacts.investment.name'),
      }}
      content={content}
      data={data?.detail}
      institution={data?.detail.institution ?? undefined}
      onUpdate={handleUpdate}
      title={t('products:investment.detail.title')}
      type={FinancialProductType.Investment}
    />
  );
};
